import React, { useCallback, useEffect, useState } from 'react';
import { api, setAuthToken, getAuthToken } from './services/api';
import BrandLogo from './components/BrandLogo';
import OficinaSidebarHistorial from './components/OficinaSidebarHistorial';
import PedidosInbox from './pages/PedidosInbox';
import PedidoReview from './pages/PedidoReview';
import PedidosPage from './pages/PedidosPage';
import ClientesPage from './pages/ClientesPage';
import ClientesImportPage from './pages/ClientesImportPage';
import InventarioPage from './pages/InventarioPage';
import { usePendingOrderAlert } from './hooks/usePendingOrderAlert';
import useRealtimeSync from './hooks/useRealtimeSync';
import { unlockOrderAlertSound } from './utils/orderAlertSound';

const USER_KEY = 'oh_pedidos_user';

const VIEW_LABELS = {
  recibidos: 'Pedidos recibidos',
  historial: 'Historial pedidos',
  clientes: 'Clientes',
  inventario: 'Inventario',
  'clientes-txt': 'Importar clientes',
};

function loadUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function LoginGate({ onUnlock }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backendOk, setBackendOk] = useState(null);

  useEffect(() => {
    api
      .ping()
      .then(() => setBackendOk(true))
      .catch(() => setBackendOk(false));
  }, []);

  async function submit(e) {
    e.preventDefault();
    unlockOrderAlertSound();
    setError('');
    setLoading(true);
    try {
      const res = await api.login(username, password);
      sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));
      const { playNewPendingSound } = await import('./utils/orderAlertSound');
      setTimeout(() => playNewPendingSound({ title: 'Sonido OK', short: true }), 200);
      onUnlock({ token: res.data.token, usuario: res.data?.usuario || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pin-screen pin-screen-pc">
      <div className="pin-screen-glow" aria-hidden="true" />
      <div className="pin-shell">
        <aside className="pin-brand-pane">
          <BrandLogo variant="pin" />
          <p className="pin-kicker">¡Oh! Belleza</p>
          <h1>Oficina</h1>
          <p className="pin-sub">Revisa y aprueba pedidos desde el escritorio antes de enviarlos a bodega.</p>
        </aside>
        <div className="pin-card pin-card-pc">
          <span className="pin-role">Aprobar pedidos</span>
          <h2 className="pin-form-title">Iniciar sesión</h2>
          <p className="pin-form-hint">Usuario y contraseña de oficina</p>
          {backendOk === false ? (
            <p className="error backend-warn">
              No hay conexión con el servidor. Enciende el API y vuelve a intentar.
            </p>
          ) : null}
          <form className="pin-form" onSubmit={submit}>
            <label className="pin-label" htmlFor="oficina-username">
              Usuario
            </label>
            <div className="pin-input-wrap">
              <input
                id="oficina-username"
                className="pin-input-text"
                type="text"
                autoComplete="username"
                placeholder="usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </div>
            <label className="pin-label" htmlFor="oficina-password">
              Contraseña
            </label>
            <div className="pin-input-wrap">
              <input
                id="oficina-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="pin-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="pin-submit" disabled={loading || !username.trim() || !password}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(() => {
    const savedToken = getAuthToken();
    if (!savedToken) return null;
    setAuthToken(savedToken);
    return { token: savedToken, usuario: loadUser() };
  });
  const [view, setView] = useState('recibidos');
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState({ message: '', type: 'ok' });
  const [checkingSession, setCheckingSession] = useState(Boolean(getAuthToken()));

  const token = session?.token || '';

  function showToast(message, type = 'ok') {
    setToast({ message: String(message || ''), type: type === 'error' ? 'error' : 'ok' });
  }

  const pendientesCount = usePendingOrderAlert(token, {
    refreshKey,
    enabled: Boolean(token) && !checkingSession,
    onNewPending: (pedido, n) => {
      const num = pedido?.numero || 'nuevo';
      showToast(n > 1 ? `${n} pedidos nuevos por aprobar` : `Pedido ${num} — por aprobar`);
      setRefreshKey((k) => k + 1);
    },
  });

  useEffect(() => {
    const saved = getAuthToken();
    if (!saved) {
      setCheckingSession(false);
      return undefined;
    }
    setAuthToken(saved);
    api
      .me()
      .then((res) => {
        sessionStorage.setItem(USER_KEY, JSON.stringify(res.data?.usuario || null));
        setSession({ token: saved, usuario: res.data?.usuario || null });
      })
      .catch(() => {
        api.logout().catch(() => {});
        sessionStorage.removeItem(USER_KEY);
        setSession(null);
      })
      .finally(() => setCheckingSession(false));
    return undefined;
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    const unlock = () => unlockOrderAlertSound();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [token]);

  useEffect(() => {
    if (!toast.message) return undefined;
    const t = setTimeout(() => setToast({ message: '', type: 'ok' }), 4500);
    return () => clearTimeout(t);
  }, [toast.message]);

  const getToken = useCallback(() => getAuthToken(), []);
  const onRealtimeInvalidate = useCallback((domains) => {
    if (domains.includes('order') || domains.includes('client') || domains.includes('seller')) {
      setRefreshKey((k) => k + 1);
    }
  }, []);
  const onRealtimeReconnect = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);
  useRealtimeSync({
    app: 'oficina',
    enabled: Boolean(token) && !checkingSession,
    getToken,
    onInvalidate: onRealtimeInvalidate,
    onReconnect: onRealtimeReconnect,
  });

  function logout() {
    api.logout().catch(() => {});
    sessionStorage.removeItem(USER_KEY);
    setSession(null);
    setSelectedId(null);
    setView('recibidos');
  }

  if (checkingSession) {
    return (
      <div className="pin-screen">
        <p className="muted">Verificando sesión...</p>
      </div>
    );
  }

  if (!session?.token) return <LoginGate onUnlock={setSession} />;

  return (
    <div className="pedidos-app">
      <aside className="pedidos-sidebar">
        <BrandLogo variant="sidebar" />
        <p className="sidebar-tag">Portal Pedidos</p>
        {session.usuario?.nombre ? (
          <p className="sidebar-user">{session.usuario.nombre}</p>
        ) : null}
        {pendientesCount > 0 ? (
          <p className="sidebar-pendientes-badge">
            {pendientesCount} pendiente{pendientesCount !== 1 ? 's' : ''} por revisar
          </p>
        ) : null}
        <nav className="oficina-nav">
          {Object.entries(VIEW_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={view === key ? 'active' : ''}
              onClick={() => {
                setView(key);
                if (key !== 'recibidos') setSelectedId(null);
              }}
            >
              {label}
              {key === 'recibidos' && pendientesCount > 0 ? (
                <span className="nav-badge">{pendientesCount}</span>
              ) : null}
            </button>
          ))}
        </nav>
        {view === 'recibidos' ? (
          <OficinaSidebarHistorial
            pin={token}
            selectedId={selectedId}
            onSelect={setSelectedId}
            refreshKey={refreshKey}
          />
        ) : view === 'historial' || view === 'clientes' ? (
          <p className="sidebar-hint">
            {view === 'historial'
              ? 'Filtros, métricas, por vendedor/bodega y detalle completo.'
              : 'Lista de clientes y sedes anidadas. Crea sedes con + Sede.'}
          </p>
        ) : null}
        <button type="button" className="logout" onClick={logout}>
          Cerrar sesión
        </button>
      </aside>
      <main className="pedidos-main">
        {view === 'recibidos' ? (
          <div className="pedidos-layout">
            <section className="pedidos-list-pane">
              <PedidosInbox
                pin={token}
                selectedId={selectedId}
                onSelect={setSelectedId}
                refreshKey={refreshKey}
              />
            </section>
            <section className="pedidos-detail-pane">
              {selectedId ? (
                <PedidoReview
                  id={selectedId}
                  pin={token}
                  onBack={() => setSelectedId(null)}
                  onUpdated={() => {
                    setRefreshKey((k) => k + 1);
                    showToast('Pedido actualizado');
                  }}
                />
              ) : (
                <div className="detail-placeholder">
                  <h3>Selecciona un pedido</h3>
                </div>
              )}
            </section>
          </div>
        ) : null}
        {view === 'historial' ? (
          <PedidosPage
            key={`historial-${refreshKey}`}
            pin={token}
            onUpdated={() => {
              setRefreshKey((k) => k + 1);
              showToast('Pedido actualizado');
            }}
          />
        ) : null}
        {view === 'clientes' ? <ClientesPage pin={token} onToast={showToast} /> : null}
        {view === 'inventario' ? <InventarioPage pin={token} onToast={showToast} /> : null}
        {view === 'clientes-txt' ? <ClientesImportPage pin={token} onToast={showToast} /> : null}
      </main>
      {toast.message ? (
        <div className={`toast toast-${toast.type === 'error' ? 'error' : 'ok'}`}>{toast.message}</div>
      ) : null}
    </div>
  );
}
